import type { CSSProperties } from 'react'

import { Heart } from 'lucide-react'
import { cn } from '@/lib/utils'

/* Fixed positions and timings so the server and the browser render the same hearts. */
const HEARTS = [
    { left: 4, size: 18, duration: 15, delay: 2, sway: 28, opacity: 0.55, tone: 0 },
    { left: 11, size: 12, duration: 12, delay: 7, sway: -22, opacity: 0.45, tone: 1 },
    { left: 18, size: 26, duration: 18, delay: 11, sway: 34, opacity: 0.4, tone: 2 },
    { left: 26, size: 14, duration: 13, delay: 4, sway: -18, opacity: 0.6, tone: 0 },
    { left: 33, size: 20, duration: 16, delay: 9, sway: 26, opacity: 0.5, tone: 1 },
    { left: 41, size: 10, duration: 11, delay: 1, sway: -14, opacity: 0.55, tone: 2 },
    { left: 48, size: 22, duration: 17, delay: 13, sway: 30, opacity: 0.45, tone: 0 },
    { left: 56, size: 16, duration: 14, delay: 6, sway: -26, opacity: 0.55, tone: 1 },
    { left: 63, size: 28, duration: 19, delay: 3, sway: 22, opacity: 0.35, tone: 2 },
    { left: 70, size: 12, duration: 12, delay: 10, sway: -20, opacity: 0.6, tone: 0 },
    { left: 77, size: 19, duration: 15, delay: 8, sway: 28, opacity: 0.5, tone: 1 },
    { left: 84, size: 24, duration: 18, delay: 5, sway: -30, opacity: 0.4, tone: 2 },
    { left: 90, size: 14, duration: 13, delay: 12, sway: 18, opacity: 0.55, tone: 0 },
    { left: 96, size: 18, duration: 16, delay: 0, sway: -24, opacity: 0.45, tone: 1 }
]

const TONES = {
    dark: ['text-petal', 'text-[#ff9ab4]', 'text-gold'],
    light: ['text-petal/70', 'text-[#f7a1b8]', 'text-petal-deep/50']
}

interface FloatingHeartsProps {
    tone?: 'dark' | 'light'
    count?: number
    className?: string
}

/** Hearts drifting up behind a section. Purely decorative. */
export default function FloatingHearts({ tone = 'dark', count = HEARTS.length, className }: FloatingHeartsProps) {
    return (
        <div aria-hidden className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}>
            {HEARTS.slice(0, count).map((heart, index) => (
                <span
                    key={index}
                    className={cn('absolute -bottom-12 animate-float-heart motion-reduce:hidden', TONES[tone][heart.tone])}
                    style={{
                        'left': `${heart.left}%`,
                        '--heart-duration': `${heart.duration}s`,
                        '--heart-delay': `-${heart.delay}s`,
                        '--heart-sway': `${heart.sway}px`,
                        '--heart-opacity': heart.opacity
                    } as CSSProperties}
                >
                    <Heart className='fill-current' strokeWidth={0} style={{ width: heart.size, height: heart.size }} />
                </span>
            ))}
        </div>
    )
}
